/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * NotificationPreferencesPage
 * ----------------------------
 * Client-facing page with two sections:
 * 1. Notification Inbox — paginated list with read/unread, mark read, mark all read, filters.
 * 2. Notification Preferences — toggle-card based UX for managing per-event-type delivery preferences.
 *
 * UX principles (Steve Krug — "Don't Make Me Think"):
 * - Inbox shows clear visual hierarchy with unread dots, time ago, expandable detail.
 * - Preferences use card-based layout with channel toggles — no raw forms.
 * - Empty states provide clear guidance on what will appear.
 * - Filter bar on inbox for quick read/unread filtering.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ProgressBarDialog from '../components/common/ProgressBar';
import { useNotifications } from '../context/NotificationContext';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

const CHANNEL_TYPES = [
    { value: 'in_app', label: 'In-App', icon: 'bi-app-indicator', description: 'Notifications inside the app' },
    { value: 'email', label: 'Email', icon: 'bi-envelope', description: 'Email to your inbox' },
    { value: 'sms', label: 'SMS', icon: 'bi-chat-dots', description: 'Text messages' },
    { value: 'websocket', label: 'Real-time', icon: 'bi-broadcast', description: 'Live push updates' },
    { value: 'webhook', label: 'Webhook', icon: 'bi-link-45deg', description: 'HTTP callbacks' }
];

const CHANNEL_ICONS = Object.fromEntries(CHANNEL_TYPES.map(c => [c.value, c.icon]));

function formatTimeAgo(dateStr) {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * @param {boolean} [showPreferences=true] The Preferences tab. The athlete feed
 *   (/feed/notifications) turns it off for now; the Dashboard keeps it.
 */
export default function NotificationPreferencesPage({ showPreferences = true } = {}) {
    const { theme } = useTheme();
    const { showAlertDialog } = useAlertDialog();
    const applicationId = envConfig.applicationId;
    const location = useLocation();
    const navigate = useNavigate();
    const highlightId = location.state?.highlightId ?? null;
    const notifRowRefs = useRef({});

    // Live unread count + actions from shared context (driven by WebSocket).
    // Context keeps the bell badge in sync; handlers below also call these
    // so the header badge updates immediately when the user acts on this page.
    // recentNotifications is watched so that WS-pushed notifications appear
    // in the inbox instantly without a page refresh.
    const {
        unreadCount,
        recentNotifications,
        markRead: contextMarkRead,
        markAllRead: contextMarkAllRead,
    } = useNotifications();

    const [activeTab, setActiveTab] = useState('inbox');
    const [loading, setLoading] = useState(true);

    // Inbox state
    const [notifications, setNotifications] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [inboxFilter, setInboxFilter] = useState('all'); // all | unread | read
    const [inboxPage, setInboxPage] = useState(1);
    const [inboxHasMore, setInboxHasMore] = useState(false);
    const [inboxLoadingMore, setInboxLoadingMore] = useState(false);

    const INBOX_PAGE_SIZE = 25;

    // Preferences state
    const [preferences, setPreferences] = useState([]);
    const [showPrefForm, setShowPrefForm] = useState(false);
    const [editingPref, setEditingPref] = useState(null);
    const [prefForm, setPrefForm] = useState({ event_type: '', channels: [], muted: false });
    const [prefErrors, setPrefErrors] = useState({});

    const [refreshKey, setRefreshKey] = useState(0);

    // ── Fetch Inbox ──────────────────────────────
    const fetchInbox = useCallback(async () => {
        try {
            const notifResp = await apiHelper.apiCall(
                `/arcee/notifications/?page=1&page_size=${INBOX_PAGE_SIZE}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ application_id: parseInt(applicationId, 10) }),
                }
            );
            if (notifResp.ok) {
                const data = await notifResp.json();
                const items = Array.isArray(data) ? data : (data.results || []);
                setNotifications(items);
                setInboxPage(1);
                setInboxHasMore(
                    Array.isArray(data) ? false : Boolean(data.next)
                );
            } else {
                console.error('Notifications API error:', notifResp.status);
                setNotifications([]);
                setInboxHasMore(false);
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
            setNotifications([]);
            setInboxHasMore(false);
        }
    }, [applicationId]);

    const loadMoreInbox = async () => {
        if (inboxLoadingMore || !inboxHasMore) return;
        setInboxLoadingMore(true);
        const nextPage = inboxPage + 1;
        try {
            const notifResp = await apiHelper.apiCall(
                `/arcee/notifications/?page=${nextPage}&page_size=${INBOX_PAGE_SIZE}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ application_id: parseInt(applicationId, 10) }),
                }
            );
            if (notifResp.ok) {
                const data = await notifResp.json();
                const items = Array.isArray(data) ? data : (data.results || []);
                setNotifications(prev => {
                    const existingIds = new Set(prev.map(n => n.id));
                    return [...prev, ...items.filter(n => !existingIds.has(n.id))];
                });
                setInboxPage(nextPage);
                setInboxHasMore(Array.isArray(data) ? false : Boolean(data.next));
            }
        } catch (error) {
            console.error('Failed to load more notifications:', error);
        } finally {
            setInboxLoadingMore(false);
        }
    };

    // ── Fetch Preferences ──────────────────────────────
    const fetchPreferences = useCallback(async () => {
        try {
            const resp = await apiHelper.apiCall('/arcee/notification-preferences/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ application_id: parseInt(applicationId, 10) })
            });
            if (resp.ok) {
                const data = await resp.json();
                setPreferences(Array.isArray(data) ? data : (data.results || []));
            } else {
                setPreferences([]);
            }
        } catch (error) {
            console.error('Failed to fetch preferences:', error);
            setPreferences([]);
        }
    }, [applicationId]);

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchInbox(), showPreferences ? fetchPreferences() : Promise.resolve()]).finally(() => setLoading(false));
    }, [fetchInbox, fetchPreferences, refreshKey, showPreferences]);

    // When a new notification arrives via WebSocket, the context prepends it to
    // recentNotifications. Sync it into the local inbox list so the page updates
    // in real-time without a refresh. Skip during initial load to avoid races
    // with fetchInbox(); skip if the item is already in the list (duplicate guard).
    useEffect(() => {
        if (loading || !recentNotifications.length) return;
        const latest = recentNotifications[0];
        setNotifications(prev => {
            if (prev.some(n => n.id === latest.id)) return prev;
            return [latest, ...prev];
        });
    }, [recentNotifications, loading]);

    // When navigated from the bell dropdown with a highlightId:
    // auto-expand the target notification, mark it as read, and scroll to it.
    useEffect(() => {
        if (loading || !highlightId || !notifications.length) return;
        const target = notifications.find(n => n.id === highlightId);
        if (!target) return;
        setActiveTab('inbox');
        setExpandedId(highlightId);
        if (!target.is_read) handleMarkRead(highlightId);
        const el = notifRowRefs.current[highlightId];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Clear state from history so a page refresh doesn't re-trigger this
        navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, notifications, highlightId]);

    // ── Filtered notifications ──────────────────────────
    const filteredNotifications = notifications.filter(n => {
        if (inboxFilter === 'unread') return !n.is_read;
        if (inboxFilter === 'read') return n.is_read;
        return true;
    });

    // ── Inbox Actions ──────────────────────────────
    // contextMarkRead / contextMarkAllRead update the header bell badge in real-time.
    // setRefreshKey re-fetches the local inbox list so this page stays consistent.
    const handleMarkRead = async (notifId) => {
        try {
            await contextMarkRead(notifId);
            setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await contextMarkAllRead();
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    // ── Preference CRUD ──────────────────────────────
    const resetPrefForm = () => {
        setPrefForm({ event_type: '', channels: [], muted: false });
        setPrefErrors({});
        setEditingPref(null);
        setShowPrefForm(false);
    };

    const openEditPref = (p) => {
        setPrefForm({
            event_type: p.event_type || '',
            channels: p.channels || [],
            muted: p.muted ?? false
        });
        setPrefErrors({});
        setEditingPref(p);
        setShowPrefForm(true);
    };

    const handlePrefChannelToggle = (chType) => {
        setPrefForm(prev => ({
            ...prev,
            channels: prev.channels.includes(chType)
                ? prev.channels.filter(c => c !== chType)
                : [...prev.channels, chType]
        }));
    };

    const validatePrefForm = () => {
        const errs = {};
        if (!prefForm.event_type.trim()) errs.event_type = 'Event type is required.';
        if (prefForm.channels.length === 0 && !prefForm.muted) errs.channels = 'Select at least one channel or mute this event.';
        setPrefErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSavePref = async () => {
        if (!validatePrefForm()) return;

        const payload = {
            application: parseInt(applicationId, 10),
            event_type: prefForm.event_type,
            channels: prefForm.channels,
            muted: prefForm.muted
        };

        try {
            let resp;
            if (editingPref) {
                resp = await apiHelper.apiCall(`/arcee/notification-preferences/${editingPref.id}/`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                resp = await apiHelper.apiCall('/arcee/notification-preferences/create/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                if (typeof err === 'object' && !err.error) {
                    const backendErrs = {};
                    for (const [key, val] of Object.entries(err)) {
                        backendErrs[key] = Array.isArray(val) ? val.join(', ') : String(val);
                    }
                    setPrefErrors(prev => ({ ...prev, ...backendErrs }));
                    return;
                }
                throw new Error(err.error || JSON.stringify(err) || 'Failed to save preference');
            }
            resetPrefForm();
            setRefreshKey(k => k + 1);
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    const handleDeletePref = (p) => {
        showAlertDialog({
            title: 'Remove Preference',
            message: `Remove preference for "${p.event_type}"? You will receive default notifications for this event.`,
            type: 'warning',
            showCancel: true,
            onOk: async () => {
                try {
                    const resp = await apiHelper.apiCall(`/arcee/notification-preferences/${p.id}/`, { method: 'DELETE' });
                    if (!resp.ok) throw new Error('Failed to delete');
                    setRefreshKey(k => k + 1);
                } catch (error) {
                    showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
                }
            }
        });
    };

    const handleToggleMute = async (p) => {
        try {
            const resp = await apiHelper.apiCall(`/arcee/notification-preferences/${p.id}/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ muted: !p.muted })
            });
            if (resp.ok) setRefreshKey(k => k + 1);
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        }
    };

    // Inline error helper
    const FieldError = ({ error }) => error ? (
        <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
            <i className="bi bi-exclamation-circle me-1"></i>{error}
        </div>
    ) : null;

    const tabStyle = (isActive) => ({
        padding: '10px 24px',
        fontSize: '14px',
        fontWeight: 600,
        border: 'none',
        borderBottom: isActive ? `3px solid ${theme.primary}` : '3px solid transparent',
        background: 'transparent',
        color: isActive ? theme.primary : '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s'
    });

    const filterBtnStyle = (isActive) => ({
        padding: '4px 14px',
        fontSize: '12px',
        fontWeight: 500,
        border: isActive ? `1px solid ${theme.primary}` : '1px solid #e2e8f0',
        background: isActive ? `${theme.primary}10` : '#fff',
        color: isActive ? theme.primary : '#64748b',
        borderRadius: '20px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    });

    if (loading) {
        return <ProgressBarDialog open={true} text_message="Loading notifications..." />;
    }

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 16px' }}>
            {/* Page Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h4 className="fw-bold mb-1" style={{ color: '#1e293b' }}>
                        <i className="bi bi-bell me-2"></i>Notifications
                    </h4>
                    <p className="text-muted small mb-0">
                        {showPreferences ? 'View your notifications and manage delivery preferences.' : 'View your notifications.'}
                    </p>
                </div>
                {unreadCount > 0 && (
                    <span className="badge rounded-pill px-3 py-2"
                        style={{ background: theme.primary, color: '#fff', fontSize: '13px' }}>
                        {unreadCount} unread
                    </span>
                )}
            </div>

            {/* Tabs — only when there is a second tab to switch to */}
            {showPreferences && (
            <div className="d-flex border-bottom mb-4">
                <button style={tabStyle(activeTab === 'inbox')} onClick={() => setActiveTab('inbox')}>
                    <i className="bi bi-inbox me-2"></i>Inbox
                    {unreadCount > 0 && (
                        <span className="badge bg-danger ms-2 rounded-pill" style={{ fontSize: '10px' }}>{unreadCount}</span>
                    )}
                </button>
                <button style={tabStyle(activeTab === 'preferences')} onClick={() => setActiveTab('preferences')}>
                    <i className="bi bi-sliders me-2"></i>Preferences
                    {preferences.length > 0 && (
                        <span className="badge bg-light text-dark ms-2 rounded-pill border" style={{ fontSize: '10px' }}>{preferences.length}</span>
                    )}
                </button>
            </div>
            )}

            {/* ── Inbox Tab ──────────────────────────── */}
            {activeTab === 'inbox' && (
                <div>
                    {/* Filter bar */}
                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                        <div className="d-flex gap-2">
                            <button style={filterBtnStyle(inboxFilter === 'all')} onClick={() => setInboxFilter('all')}>
                                All ({notifications.length})
                            </button>
                            <button style={filterBtnStyle(inboxFilter === 'unread')} onClick={() => setInboxFilter('unread')}>
                                <i className="bi bi-circle-fill me-1" style={{ fontSize: '6px', verticalAlign: 'middle' }}></i>
                                Unread ({unreadCount})
                            </button>
                            <button style={filterBtnStyle(inboxFilter === 'read')} onClick={() => setInboxFilter('read')}>
                                Read ({notifications.length - unreadCount})
                            </button>
                        </div>
                        {unreadCount > 0 && (
                            <button
                                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                                onClick={handleMarkAllRead}
                                style={{ borderRadius: theme.buttonRadius, borderColor: theme.primary, color: theme.primary, fontSize: '13px' }}
                            >
                                <i className="bi bi-check-all"></i>
                                Mark all as read
                            </button>
                        )}
                    </div>

                    {filteredNotifications.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                style={{ width: 72, height: 72, background: '#f1f5f9' }}>
                                <i className="bi bi-bell-slash fs-2 text-muted opacity-50"></i>
                            </div>
                            <p className="text-muted mb-1">
                                {inboxFilter === 'all' ? 'No notifications yet' :
                                    inboxFilter === 'unread' ? 'All caught up!' :
                                        'No read notifications'}
                            </p>
                            <p className="text-muted small">
                                {inboxFilter === 'all'
                                    ? 'Notifications will appear here when events occur in your application.'
                                    : inboxFilter === 'unread'
                                        ? 'You have no unread notifications right now.'
                                        : 'Read notifications will appear here after you view them.'}
                            </p>
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-2" id="inbox-list">
                            {filteredNotifications.map(n => (
                                <div
                                    key={n.id}
                                    ref={el => { notifRowRefs.current[n.id] = el; }}
                                    className="card border-0 shadow-sm"
                                    style={{
                                        borderRadius: theme.cardRadius,
                                        borderLeft: n.id === highlightId
                                            ? `4px solid ${theme.secondary || '#f59e0b'}`
                                            : n.is_read ? '4px solid #e2e8f0' : `4px solid ${theme.primary}`,
                                        cursor: 'pointer',
                                        background: n.id === highlightId
                                            ? `${theme.secondary || '#f59e0b'}12`
                                            : n.is_read ? '#fff' : `${theme.primary}08`,
                                        transition: 'transform 0.1s',
                                    }}
                                    onClick={() => setExpandedId(expandedId === n.id ? null : n.id)}
                                >
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-start justify-content-between">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    {!n.is_read && (
                                                        <span style={{
                                                            width: 8, height: 8, borderRadius: '50%',
                                                            background: theme.primary, flexShrink: 0
                                                        }}></span>
                                                    )}
                                                    <span className={`fw-${n.is_read ? 'normal' : 'bold'}`} style={{ fontSize: '14px' }}>
                                                        {n.title || n.subject || 'Notification'}
                                                    </span>
                                                    <span className="badge bg-light text-dark border" style={{ fontSize: '10px' }}>
                                                        {n.event_type}
                                                    </span>
                                                    {n.priority === 'high' && (
                                                        <span className="badge" style={{ background: '#fee2e2', color: '#991b1b', fontSize: '10px' }}>
                                                            <i className="bi bi-flag-fill me-1"></i>High
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-muted small mb-0" style={{
                                                    overflow: expandedId === n.id ? 'visible' : 'hidden',
                                                    textOverflow: expandedId === n.id ? 'unset' : 'ellipsis',
                                                    whiteSpace: expandedId === n.id ? 'normal' : 'nowrap',
                                                    maxWidth: expandedId === n.id ? 'none' : '500px'
                                                }}>
                                                    {n.body || ''}
                                                </p>
                                            </div>
                                            <div className="d-flex align-items-center gap-2 ms-3">
                                                <span className="text-muted small" style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>
                                                    {formatTimeAgo(n.created_at)}
                                                </span>
                                                {!n.is_read && (
                                                    <button
                                                        className="btn btn-sm btn-outline-primary border-0"
                                                        onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id); }}
                                                        title="Mark as read"
                                                        style={{ padding: '2px 6px' }}
                                                    >
                                                        <i className="bi bi-check2"></i>
                                                    </button>
                                                )}
                                                <i className={`bi bi-chevron-${expandedId === n.id ? 'up' : 'down'} text-muted`} style={{ fontSize: '12px' }}></i>
                                            </div>
                                        </div>

                                        {/* Expanded details */}
                                        {expandedId === n.id && (
                                            <div className="mt-3 pt-3 border-top">
                                                <div className="d-flex flex-wrap gap-3 small text-muted">
                                                    {n.channels_delivered && n.channels_delivered.length > 0 && (
                                                        <div className="d-flex align-items-center gap-1">
                                                            <span className="fw-semibold">Delivered via:</span>
                                                            {n.channels_delivered.map(ch => (
                                                                <span key={ch} className="badge bg-light text-dark border text-capitalize" style={{ fontSize: '10px' }}>
                                                                    <i className={`bi ${CHANNEL_ICONS[ch] || 'bi-bell'} me-1`}></i>
                                                                    {ch.replace(/_/g, ' ')}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <i className="bi bi-clock me-1"></i>
                                                        {new Date(n.created_at).toLocaleString()}
                                                    </div>
                                                    {n.delivered_at && (
                                                        <div>
                                                            <i className="bi bi-check-circle text-success me-1"></i>
                                                            Delivered: {new Date(n.delivered_at).toLocaleString()}
                                                        </div>
                                                    )}
                                                    {n.failure_reason && (
                                                        <div className="text-danger">
                                                            <i className="bi bi-x-circle me-1"></i>
                                                            {n.failure_reason}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Load more — only visible when not filtering (filter hides items client-side) */}
                    {inboxFilter === 'all' && inboxHasMore && (
                        <div className="text-center mt-3">
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={loadMoreInbox}
                                disabled={inboxLoadingMore}
                                style={{ borderRadius: theme.buttonRadius, minWidth: 120 }}
                            >
                                {inboxLoadingMore
                                    ? <><span className="spinner-border spinner-border-sm me-2" role="status" /><span>Loading…</span></>
                                    : <><i className="bi bi-arrow-down-circle me-2" />Load more</>
                                }
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ── Preferences Tab ──────────────────────────── */}
            {showPreferences && activeTab === 'preferences' && (
                <div>
                    {/* Header with explanation */}
                    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: theme.cardRadius }}>
                        <div className="card-body p-4">
                            <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                                <div>
                                    <h6 className="fw-bold mb-1" style={{ color: '#1e293b' }}>
                                        <i className="bi bi-sliders me-2 text-primary"></i>
                                        Delivery Preferences
                                    </h6>
                                    <p className="text-muted small mb-0">
                                        Control how you receive notifications for specific events.
                                        When you add a preference, you choose which channels deliver that event type.
                                        Events without a preference use the application's default settings.
                                    </p>
                                </div>
                                <button
                                    className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                    onClick={() => { resetPrefForm(); setShowPrefForm(true); }}
                                    style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius }}
                                >
                                    <i className="bi bi-plus-lg"></i>
                                    Add Preference
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Preference Form */}
                    {showPrefForm && (
                        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: theme.cardRadius, borderTop: `3px solid ${theme.primary}` }}>
                            <div className="card-body p-4">
                                <h6 className="fw-bold small text-uppercase text-muted mb-3">
                                    {editingPref ? 'Edit Preference' : 'New Preference'}
                                </h6>
                                <div className="mb-3">
                                    <label className="small fw-bold text-muted mb-1">Event Type <span className="text-danger">*</span></label>
                                    <input className={`form-control form-control-sm${prefErrors.event_type ? ' is-invalid' : ''}`}
                                        value={prefForm.event_type}
                                        onChange={e => setPrefForm(f => ({ ...f, event_type: e.target.value }))}
                                        placeholder="e.g. order_placed, payment_received"
                                        disabled={!!editingPref}
                                        style={{ borderRadius: theme.inputRadius || '6px' }} />
                                    <FieldError error={prefErrors.event_type} />
                                    <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                                        The event identifier that triggers this notification.
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="small fw-bold text-muted mb-2 d-block">Delivery Channels</label>
                                    <div className="row g-2">
                                        {CHANNEL_TYPES.map(ct => {
                                            const isSelected = prefForm.channels.includes(ct.value);
                                            return (
                                                <div className="col-6 col-md-4" key={ct.value}>
                                                    <div
                                                        className={`p-3 rounded border text-center h-100 ${isSelected ? 'border-primary shadow-sm' : 'bg-light'}`}
                                                        style={{
                                                            cursor: prefForm.muted ? 'not-allowed' : 'pointer',
                                                            opacity: prefForm.muted ? 0.5 : 1,
                                                            transition: 'all 0.2s',
                                                            background: isSelected ? `${theme.primary}08` : undefined
                                                        }}
                                                        onClick={() => !prefForm.muted && handlePrefChannelToggle(ct.value)}
                                                    >
                                                        <i className={`bi ${ct.icon} d-block mb-1`} style={{ fontSize: '1.3rem', color: isSelected ? theme.primary : '#94a3b8' }}></i>
                                                        <div className="small fw-semibold">{ct.label}</div>
                                                        <div className="text-muted" style={{ fontSize: '10px' }}>{ct.description}</div>
                                                        {isSelected && (
                                                            <i className="bi bi-check-circle-fill text-primary mt-1" style={{ fontSize: '12px' }}></i>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <FieldError error={prefErrors.channels} />
                                </div>

                                <div className="form-check form-switch mb-3 p-3 bg-light rounded border">
                                    <input type="checkbox" className="form-check-input"
                                        checked={prefForm.muted} role="switch"
                                        onChange={e => setPrefForm(f => ({ ...f, muted: e.target.checked }))}
                                        id="prefMuted" />
                                    <label className="form-check-label" htmlFor="prefMuted">
                                        <span className="small fw-bold">
                                            <i className="bi bi-bell-slash me-1"></i>
                                            Mute this event type
                                        </span>
                                        <br />
                                        <span className="text-muted" style={{ fontSize: '11px' }}>
                                            When muted, you won't receive notifications for this event through any channel.
                                        </span>
                                    </label>
                                </div>

                                <div className="d-flex gap-2">
                                    <button className="btn btn-sm btn-primary" onClick={handleSavePref}
                                        style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius }}>
                                        {editingPref ? 'Update Preference' : 'Save Preference'}
                                    </button>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={resetPrefForm}
                                        style={{ borderRadius: theme.buttonRadius }}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Preferences List */}
                    {preferences.length === 0 && !showPrefForm ? (
                        <div className="text-center py-5">
                            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                style={{ width: 72, height: 72, background: '#f1f5f9' }}>
                                <i className="bi bi-sliders fs-2 text-muted opacity-50"></i>
                            </div>
                            <p className="text-muted mb-1">No custom preferences set</p>
                            <p className="text-muted small mb-3">
                                You're receiving all notifications with default settings. Add a preference to customize delivery for specific events.
                            </p>
                            <button className="btn btn-sm btn-outline-primary"
                                onClick={() => { resetPrefForm(); setShowPrefForm(true); }}
                                style={{ borderRadius: theme.buttonRadius, borderColor: theme.primary, color: theme.primary }}>
                                <i className="bi bi-plus-lg me-1"></i>Add your first preference
                            </button>
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {preferences.map(p => (
                                <div key={p.id} className="card border-0 shadow-sm" style={{ borderRadius: theme.cardRadius }}>
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-start justify-content-between mb-3">
                                            <div>
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <code style={{ fontSize: '14px', color: theme.primary, fontWeight: 600 }}>{p.event_type}</code>
                                                    {p.muted && (
                                                        <span className="badge" style={{ background: '#fee2e2', color: '#991b1b', fontSize: '10px' }}>
                                                            <i className="bi bi-bell-slash me-1"></i>Muted
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="d-flex gap-1">
                                                <button className="btn btn-sm btn-outline-secondary border-0"
                                                    onClick={() => handleToggleMute(p)}
                                                    title={p.muted ? 'Unmute' : 'Mute'}>
                                                    <i className={`bi ${p.muted ? 'bi-bell' : 'bi-bell-slash'} text-muted`}></i>
                                                </button>
                                                <button className="btn btn-sm btn-outline-primary border-0"
                                                    onClick={() => openEditPref(p)} title="Edit">
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button className="btn btn-sm btn-outline-danger border-0"
                                                    onClick={() => handleDeletePref(p)} title="Remove">
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                        {/* Channel cards */}
                                        {!p.muted && (
                                            <div className="d-flex flex-wrap gap-2">
                                                {CHANNEL_TYPES.map(ct => {
                                                    const enabled = (p.channels || []).includes(ct.value);
                                                    return (
                                                        <div key={ct.value}
                                                            className={`d-flex align-items-center gap-2 px-3 py-2 rounded border ${enabled ? 'border-primary' : 'bg-light'}`}
                                                            style={{
                                                                opacity: enabled ? 1 : 0.4,
                                                                background: enabled ? `${theme.primary}08` : undefined,
                                                                fontSize: '13px'
                                                            }}>
                                                            <i className={`bi ${ct.icon}`} style={{ color: enabled ? theme.primary : '#94a3b8' }}></i>
                                                            <span className={enabled ? 'fw-semibold' : ''}>{ct.label}</span>
                                                            {enabled && <i className="bi bi-check text-primary" style={{ fontSize: '14px' }}></i>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {p.muted && (
                                            <div className="text-muted small">
                                                <i className="bi bi-info-circle me-1"></i>
                                                All notifications for this event are muted. Unmute to configure channels.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
